import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { TableSkeleton } from '../ui/Skeleton'
import type { RevenueTransaction } from '../../lib/api'
import {
  formatDoge,
  formatDateTime,
  REVENUE_TYPE_LABELS,
  exportToCSV,
  shibesToDoge,
} from '../../lib/utils'

interface TransactionsTableProps {
  data: RevenueTransaction[] | null
  loading: boolean
  page: number
  pageSize: number
  onPageChange: (page: number) => void
}

export default function TransactionsTable({
  data,
  loading,
  page,
  pageSize,
  onPageChange,
}: TransactionsTableProps) {
  const handleExport = () => {
    if (!data) return

    const exportData = data.map((tx) => ({
      ID: tx.id,
      Type: REVENUE_TYPE_LABELS[tx.revenue_type] || tx.revenue_type,
      Chain: tx.chain,
      'Amount (DOGE)': shibesToDoge(tx.amount).toFixed(8),
      'Original Amount (DOGE)': shibesToDoge(tx.original_amount).toFixed(8),
      'Commission Rate': `${(tx.commission_rate * 100).toFixed(1)}%`,
      'User ID': tx.user_id,
      'Campaign ID': tx.campaign_id || '',
      'Created At': tx.created_at,
    }))

    exportToCSV(exportData, `dogici-transactions-${new Date().toISOString().split('T')[0]}.csv`)
  }

  if (loading) {
    return <TableSkeleton />
  }

  const hasMore = data && data.length === pageSize
  const hasPrev = page > 0

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <CardTitle>Recent Transactions</CardTitle>
        <button
          onClick={handleExport}
          disabled={!data || data.length === 0}
          className="btn-secondary flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
        >
          <Download size={16} />
          <span className="hidden xs:inline">Export CSV</span>
          <span className="xs:hidden">Export</span>
        </button>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            No transactions found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 md:mx-0">
              <table className="w-full min-w-[500px] md:min-w-0">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 hidden md:table-cell">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Type</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">
                      Amount
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-400 hidden sm:table-cell">
                      Rate
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 hidden lg:table-cell">
                      User ID
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((tx) => (
                    <tr
                      key={tx.id}
                      className="border-b border-slate-700/50 hover:bg-slate-800/50"
                    >
                      <td className="py-3 px-4 text-sm font-mono text-slate-300 hidden md:table-cell">
                        #{tx.id}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={tx.revenue_type === 'campaign_fee' ? 'warning' : 'success'}
                        >
                          {tx.revenue_type === 'campaign_fee' ? 'Campaign' : 'Survey'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-medium text-doge-400">
                          {formatDoge(tx.amount, 4)} DOGE
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-slate-400 hidden sm:table-cell">
                        {(tx.commission_rate * 100).toFixed(0)}%
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300 hidden lg:table-cell">
                        {tx.user_id}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-400">
                        {formatDateTime(tx.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400 order-2 sm:order-1">
                Showing {page * pageSize + 1} - {page * pageSize + data.length}
              </p>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={!hasPrev}
                  className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm text-slate-300 px-3">Page {page + 1}</span>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={!hasMore}
                  className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
